import { DIFFICULTY } from "@/integrations/backend/enums";

export type ExerciseFilterable = {
  name: string;
  categories?: string[];
  primary_muscles?: string[];
  secondary_muscles?: string[];
  equipment?: string[];
  difficulties?: string[];
  muscle_actions?: string[];
  exercise_types?: string[];
  instructions?: string;
};

export type ExerciseLibraryFilterCriteria = {
  searchQuery?: string;
  movementPatterns?: string[];
  muscleGroups?: string[];
  equipment?: string[];
  muscleActions?: string[];
  difficulties?: string[];
  exerciseTypes?: string[];
};

export const EXERCISE_DIFFICULTY_OPTIONS = DIFFICULTY as unknown as readonly string[];

export function summarizeFilterValues(values: string[], placeholder: string): string {
  if (values.length === 0) return placeholder;
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]}, ${values[1]}`;
  return `${values[0]} +${values.length - 1}`;
}

export function countActiveExerciseFilters(criteria: ExerciseLibraryFilterCriteria): number {
  let count = 0;
  if (criteria.movementPatterns?.length) count++;
  if (criteria.muscleGroups?.length) count++;
  if (criteria.equipment?.length) count++;
  if (criteria.muscleActions?.length) count++;
  if (criteria.difficulties?.length) count++;
  if (criteria.exerciseTypes?.length) count++;
  return count;
}

export function matchesExerciseLibraryFilters(
  exercise: ExerciseFilterable,
  {
    searchQuery = "",
    movementPatterns = [],
    muscleGroups = [],
    equipment = [],
    muscleActions = [],
    difficulties = [],
    exerciseTypes = [],
  }: ExerciseLibraryFilterCriteria
): boolean {
  const q = searchQuery.trim().toLowerCase();
  const matchesSearch =
    q.length === 0 ||
    exercise.name.toLowerCase().includes(q) ||
    (exercise.instructions || "").toLowerCase().includes(q) ||
    (exercise.equipment || []).some((eq) => eq.toLowerCase().includes(q)) ||
    (exercise.primary_muscles || []).some((m) => String(m).toLowerCase().includes(q)) ||
    (exercise.secondary_muscles || []).some((m) => String(m).toLowerCase().includes(q));

  const matchesMovement =
    movementPatterns.length === 0 ||
    (exercise.categories || []).some((c) => movementPatterns.includes(String(c)));

  const matchesMuscleGroup =
    muscleGroups.length === 0 ||
    (exercise.primary_muscles || []).some((m) => muscleGroups.includes(String(m))) ||
    (exercise.secondary_muscles || []).some((m) => muscleGroups.includes(String(m)));

  const matchesEquipment =
    equipment.length === 0 || (exercise.equipment || []).some((eq) => equipment.includes(eq));

  const matchesMuscleActions =
    muscleActions.length === 0 ||
    (exercise.muscle_actions || []).some((a) => muscleActions.includes(String(a)));

  const matchesDifficulty =
    difficulties.length === 0 ||
    (exercise.difficulties || []).some((d) => difficulties.includes(String(d)));

  const matchesExerciseTypes =
    exerciseTypes.length === 0 ||
    (exercise.exercise_types || []).some((t) => exerciseTypes.includes(String(t)));

  return (
    matchesSearch &&
    matchesMovement &&
    matchesMuscleGroup &&
    matchesEquipment &&
    matchesMuscleActions &&
    matchesDifficulty &&
    matchesExerciseTypes
  );
}
